'use client'

import _ from 'lodash'
import { twMerge } from 'tailwind-merge'
import { useState, useEffect, useRef } from 'react'
import { ReactSortable } from 'react-sortablejs'
import { useDebounce } from 'ahooks'

import { Common } from '@cruz-com/lookup'
import { useGlobalStore } from '@/store/provider'
import { myCruzApi } from '@/api'
import { Button, Confirm } from '@/components'

type Props = {
  _id: string,
  photo_list: { id: string, url: string }[] | any
  onPhotoDone: (d: any) => void
}

export function MyVehiclePhotos({ _id, photo_list, onPhotoDone }: Props) {
  const { setToast } = useGlobalStore((state) => state)
  const mountRef = useRef<boolean>(false)
  const [photos, setPhotos] = useState<Common.Photo[] | any>(photo_list)

  useEffect(() => {
    setPhotos(photo_list)
  }, [photo_list])

  // select photos
  const [countSelected, setCountSelected] = useState(0)
  function toggleSelectPhoto(id: string) {
    const photo = _.find(photos, { id })
    if (!photo) return
    photo.selected = !photo.selected
    setPhotos(photos)
    setCountSelected(_.filter(photos, 'selected').length)
  }
  function unSelectPhotos() {
    setPhotos(_.map(photos, x => ({ ...x, selected: false })))
    setCountSelected(0)
  }

  // confirm & delete select photos
  const [showConfirm, setShowConfirm] = useState(false)
  async function deleteSelectedPhotos() {
    setShowConfirm(false)
    try {
      const { data } = await myCruzApi.vehicle.deletePhotos(_id, _.filter(photos, 'selected').map(x => _.pick(x, ['id'])))
      setCountSelected(0)
      onPhotoDone(data)
    } catch (err: any) {
      setToast({ type: 'error', message: err?.message })
    }
  }

  // sort photo
  function onSort(d: any) {
    if (mountRef.current) {
      setPhotos(d)
    }
  }

  // prevent useEffect on mount
  useEffect(() => {
    mountRef.current = true
  }, [])

  // monitor sort 
  const debouncedValue = useDebounce(photos, { wait: 500 })
  useEffect(() => {
    if (mountRef.current) {
      if (countSelected === 0 && !_.isEqual(_.map(debouncedValue, 'id'), _.map(photo_list, 'id'))) {
        saveSortedPhotos()
      }
    }
  }, [debouncedValue])

  // save sorted photos
  async function saveSortedPhotos() {
    try {
      const { data } = await myCruzApi.vehicle.sortPhotos(_id, _.map(photos, x => _.pick(x, ['id'])))
      onPhotoDone(data)
      setToast({ type: 'success', message: 'Successfully saved' })
    } catch (err: any) {
      setToast({ type: 'error', message: err?.message })
    }
  }

  if (_.isEmpty(photos)) {
    return <></>
  }

  return (
    <div className='w-full p-4 my-4 border rounded-md'>
      <Confirm
        title='Please Confirm'
        content='Are you sure you want to delete selected photo(s)?'
        open={showConfirm}
        onConfirm={() => { deleteSelectedPhotos() }}
        onCancel={() => { setShowConfirm(false) }}
      />
      <ReactSortable
        list={photos}
        setList={d => onSort(d)}
        animation={200}
        className='w-full grid grid-cols-4 md:grid-cols-3 xl:grid-cols-4 items-center gap-2'
      >
        {
          _.map(photos, (x) => {
            return (
              <div key={x.id} onClick={() => toggleSelectPhoto(x.id)}
                className={twMerge('border py-1 rounded-md', x.selected ? 'bg-red-800' : 'bg-zinc-100 dark:bg-zinc-700')}
              >
                <img src={x.p_url} className='aspect-16/10 object-contain m-auto' />
              </div>
            )
          })
        }
      </ReactSortable>
      {
        countSelected > 0 &&
        <div className='w-full mt-6 flex items-center justify-center gap-x-2'>
          <Button type='button' variant='outline' onClick={unSelectPhotos} className='w-24 h-8 px-4 rounded-full'>
            Cancel
          </Button>
          <Button type='button' variant='secondary' className='min-w-24 h-8 px-4 rounded-full' onClick={() => setShowConfirm(true)}>
            Delete {countSelected} Photo{countSelected > 1 && 's'}
          </Button>
        </div>
      }
    </div>
  )
}